/**
 * Duvar levels: Level N = generateLevel(N), seed hashSeed("duvar:<N>"). Walls are built in reverse:
 * blocks are inserted one by one and each gets an arrow whose path out is clear of the blocks
 * already placed (see buildWall). Removing blocks in reverse insertion order therefore always works, and because
 * an exit only ever frees paths, the wall stays solvable whatever order the player takes.
 *
 * From level 21 on the chapter designer (@enjoyment/core designer.ts) picks which ramp level (4–26) each wall is
 * sized and tangled like, and the chapter's twist; the seed (so the wall) and the picture still follow the real N.
 */
import { chapterTwist, designChapterOf, designLevel, hashSeed, Rng, type DesignSpec, type Picture } from "@enjoyment/core";
import { DIRS, cellIndex, type Block, type Dir, type DuvarLevel } from "./game";

/** Theme per level; flags recur often (bright, readable shapes on a wall). */
export const THEME_CYCLE = ["flags", "animals", "food", "flags", "objects", "nature", "flags", "vehicles", "music-sport"] as const;

/**
 * Chapter design (levels 21+). Floor 4: past the one-layer tutorial walls (1–3). Ceiling 26: the ramp's last step
 * (3 layers; the out-arrow share bottoms out at 25), so breathers get 6×5×2 walls, hard levels 6×6×2 and late
 * finales 3 layers. Twists are knobs the ramp already has: a chapter of 3-layer walls (`deep`, the ramp's own
 * 4–5×5×3 shape), of fewer "out" arrows so more blocks stand in each other's way (`tangle`), or of flags only
 * (`flags`, the theme cycle's most readable pictures).
 */
export const DESIGN: DesignSpec = { game: "duvar", floor: 4, ceiling: 26, twists: ["deep", "tangle", "flags"] };

/** How much the `tangle` twist lowers the out-arrow share (never below the one-layer walls' 0.1). */
const TANGLE = 0.08;

/** The ramp's 3-layer wall: 4 or 5 columns by level parity, at most 75 blocks. */
const deepWall = (n: number) => ({ w: 4 + (n % 2), h: 5, d: 3 });

/**
 * Wall size of the hand-tuned ramp (levels 1–20, and the scale the designer draws from): one layer first (a 2D
 * puzzle), then depth. Capped at 75 blocks: the game is about opening the right columns, not tapping a hundred.
 */
export function rampSize(n: number): { w: number; h: number; d: number } {
  if (n <= 1) return { w: 4, h: 4, d: 1 };
  if (n <= 3) return { w: 5, h: 5, d: 1 };
  if (n <= 8) return { w: 5, h: 5, d: 2 };
  if (n <= 15) return { w: 6, h: 5, d: 2 };
  if (n <= 25) return { w: 6, h: 6, d: 2 };
  return deepWall(n);
}

/** Wall size of level n: the ramp for 1–20, the designer's pick from 21 on. */
export function sizeFor(n: number): { w: number; h: number; d: number } {
  const d = designLevel(DESIGN, n);
  if (!d) return rampSize(n);
  return d.twist === "deep" ? deepWall(d.effective) : rampSize(d.effective);
}

/** Share of "out" (toward the player) arrows on the hand-tuned ramp; sideways arrows make the planning. */
export function outShare(n: number, d: number): number {
  return d === 1 ? Math.max(0.1, 0.45 - n * 0.06) : Math.max(0.2, 0.45 - n * 0.01);
}

/** Out-arrow share of level n's wall (depth `depth`): the ramp for 1–20, the designer's pick from 21 on. */
export function outShareFor(n: number, depth: number): number {
  const d = designLevel(DESIGN, n);
  if (!d) return outShare(n, depth);
  const base = outShare(d.effective, depth);
  return d.twist === "tangle" ? Math.max(0.1, Math.round((base - TANGLE) * 100) / 100) : base;
}

/** Theme of level n: the cycle, except flags all along a `flags` chapter. */
export function themeFor(n: number): string {
  const chapter = designChapterOf(n);
  if (chapter && chapterTwist(DESIGN, chapter) === "flags") return "flags";
  return THEME_CYCLE[(n - 1) % THEME_CYCLE.length]!;
}

function rayClear(occupied: Uint8Array, l: { w: number; h: number; d: number }, x: number, y: number, z: number, dir: Dir): boolean {
  const [dx, dy, dz] = DIRS[dir]!;
  for (let cx = x + dx, cy = y + dy, cz = z + dz; cx >= 0 && cx < l.w && cy >= 0 && cy < l.h && cz >= 0 && cz < l.d; cx += dx, cy += dy, cz += dz) {
    if (occupied[cellIndex(l, cx, cy, cz)]) return false;
  }
  return true;
}

function rayLength(l: { w: number; h: number; d: number }, x: number, y: number, z: number, dir: Dir): number {
  return dir === 0 ? l.w - 1 - x : dir === 1 ? x : dir === 2 ? l.h - 1 - y : dir === 3 ? y : l.d - 1 - z;
}

/**
 * Reverse construction. Columns fill back to front (z = 0 first), the column order is random, so
 * "out" is always a valid arrow and the build never gets stuck; sideways arrows are chosen among
 * the directions whose path is still empty, weighted toward long paths.
 */
function buildWall(rng: Rng, l: { w: number; h: number; d: number }, pOut: number): Omit<Block, "id">[] {
  const occupied = new Uint8Array(l.w * l.h * l.d);
  const nextZ = new Array<number>(l.w * l.h).fill(0);
  const open: number[] = nextZ.map((_, i) => i);
  const placed: Omit<Block, "id">[] = [];
  while (open.length) {
    const k = rng.int(open.length);
    const col = open[k]!;
    const x = col % l.w;
    const y = Math.floor(col / l.w);
    const z = nextZ[col]!;
    const valid = ([0, 1, 2, 3, 4] as Dir[]).filter((dir) => rayClear(occupied, l, x, y, z, dir));
    // Long sideways paths cross more of the wall: more blocks end up in each other's way.
    const weights = valid.map((dir) => (dir === 4 ? pOut : ((1 - pOut) / 4) * (1 + rayLength(l, x, y, z, dir))));
    let roll = rng.next() * weights.reduce((a, b) => a + b, 0);
    let pick = valid[valid.length - 1]!;
    for (let i = 0; i < valid.length; i++) {
      roll -= weights[i]!;
      if (roll <= 0) {
        pick = valid[i]!;
        break;
      }
    }
    occupied[cellIndex(l, x, y, z)] = 1;
    placed.push({ x, y, z, dir: pick });
    nextZ[col] = z + 1;
    if (z + 1 >= l.d) open.splice(k, 1);
  }
  return placed;
}

export function generateLevel(levelNumber: number): DuvarLevel {
  const n = Math.max(1, Math.floor(levelNumber));
  const seed = hashSeed(`duvar:${n}`);
  const rng = new Rng(seed);
  const size = sizeFor(n);
  const pOut = outShareFor(n, size.d);
  const wall = buildWall(rng, size, pOut);
  const blocks: Block[] = wall
    .slice()
    .sort((a, b) => a.z - b.z || a.y - b.y || a.x - b.x)
    .map((b, id) => ({ ...b, id }));
  const theme = themeFor(n);
  // k-th visit of the theme (flags chapters included), so no picture repeats until the theme's pool is used
  let pictureIndex = 0;
  for (let k = 1; k < n; k++) if (themeFor(k) === theme) pictureIndex++;
  return { n, seed, ...size, blocks, theme, pictureIndex };
}

/** Pictures of the level's theme in a stable order (also the answer-suggestion pool). */
export function themePool(level: DuvarLevel, pictures: readonly Picture[]): Picture[] {
  const pool = pictures.filter((p) => p.theme === level.theme);
  return (pool.length ? pool : [...pictures]).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The level's picture: the theme pool in a fixed shuffled order, the k-th visit takes the k-th. */
export function pickPicture(level: DuvarLevel, pictures: readonly Picture[]): Picture {
  const pool = themePool(level, pictures);
  if (!pool.length) throw new Error("empty picture pool");
  const rng = new Rng(hashSeed(`duvar:theme:${level.theme}`));
  const order = pool.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return pool[order[level.pictureIndex % order.length]!]!;
}
